/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useDateFormatter, useTranslations } from "@i18n/react";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";

import type { ThemeType } from "../../../../global/emotion";
import { radius, spacing, useTheme } from "../../../../global/emotion";
import {
  cssMediaQueryIsSafari,
  cssMediaQueryMinWidthTablet,
} from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { useConfirm } from "../../_base/modals/Dialog/Dialog.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { AuthTextInput } from "./AuthTextInput.emotion";

export const dobSchema = z.object({
  dob: z.iso.date("dob_input_error"),
});

export const dobInputFormFieldStyle: Interpolation<ThemeType> = (theme) => ({
  minHeight: spacing.l,
  width: spacing.full,
  [cssMediaQueryIsSafari]: {
    background: theme.semanticColors.neutralsVeryLow,
    borderRadius: radius.s,
  },
  [cssMediaQueryMinWidthTablet]: {
    [cssMediaQueryIsSafari]: {
      background: "unset",
      borderRadius: "unset",
    },
  },
});

export const dobButtonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "flex-end",
};

export const useConfirmUnderageDialog = () => {
  const t = useTranslations();
  const format = useDateFormatter();
  const { renderedTheme } = useTheme();

  return useConfirm<{ dob: Date }>({
    cancelButton: (onClick) => (
      <Button
        variant={renderedTheme === "dark" ? "darken" : "neutral"}
        onClick={onClick}
        isFullWidth
      >
        {t("dob_no_cancel")}
      </Button>
    ),
    description: ({ dob }) =>
      t.rich("dob_confirm_date_alert_message", {
        0: format(dob, "MMM dd, yyyy"),
        b: (chunks) => <strong>{chunks}</strong>,
      }),
    primaryButton: (onClick) => (
      <Button
        variant={renderedTheme === "dark" ? "darken" : "neutral"}
        onClick={onClick}
        isFullWidth
      >
        {t("dob_yes_correct")}
      </Button>
    ),
    title: () => t("dob_confirm_date_alert_title"),
    buttonFormat: "vertical-reverse",
  });
};

type AuthDOBFormContentProps = {
  form: UseFormReturn<z.infer<typeof dobSchema>>;
  errors: import("react-hook-form").FieldErrors<z.infer<typeof dobSchema>>;
  errorsCSS: Record<keyof z.infer<typeof dobSchema>, Interpolation>;
  fieldsCSS: Record<keyof z.infer<typeof dobSchema>, Interpolation>;
  inputProps: Partial<
    import("../../../types/hallowElementProps.types").HallowElementProps<"input">
  >;
  titleTestId?: string;
};

export const AuthDOBFormContent = ({
  form,
  errors,
  errorsCSS,
  fieldsCSS,
  inputProps,
  titleTestId,
}: AuthDOBFormContentProps) => {
  const t = useTranslations();
  return (
    <>
      <Text type="headline" size="m" as="h2" data-testid={titleTestId}>
        {t("dob_what_is_your_birthday")}
      </Text>
      <FormField
        control={form.control}
        name="dob"
        showErrorMessage={false}
        defaultValue={""}
        render={({ field }) => (
          <AuthTextInput
            required
            autoFocus
            autoComplete="bday"
            autoCorrect="off"
            css={fieldsCSS[field.name]}
            type="date"
            {...inputProps}
            {...field}
          />
        )}
        css={dobInputFormFieldStyle}
      />
      <AuthError css={errorsCSS.dob}>
        {errors?.dob?.message ? t(errors.dob.message) : " "}
      </AuthError>
    </>
  );
};
